import { DOCUMENT } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { ACCOUNT, NAV, RANGE } from '../content';
import { Popups } from '../popups';
import { RangeTab } from '../range-tab';

/** The icon beside each category in the Collections menu, by the category's id: path data for a 24×24 stroke icon. */
const CATEGORY_ICONS: Record<string, string> = {
  living: 'M6 11V8.5A2.5 2.5 0 0 1 8.5 6h7A2.5 2.5 0 0 1 18 8.5V11M4 18v-5a2 2 0 0 1 4 0v2h8v-2a2 2 0 0 1 4 0v5ZM6.5 18v2M17.5 18v2',
  theater: 'M3 5h18v11H3ZM8 20h8M12 16v4M10.5 8.5v4l3.5-2Z',
  lounger: 'M3 17h18M5.5 17v2.5M18.5 17v2.5M4 17V9.5a1.5 1.5 0 0 1 3 0V13h12a2 2 0 0 1 2 2v2',
  multiplex: 'M4 4h16v16H4ZM8 4v16M16 4v16M4 9h4M4 15h4M16 9h4M16 15h4',
  segments: 'M4 4h7v7H4ZM13 4h7v7h-7ZM4 13h7v7H4ZM13 13h7v7h-7Z',
  massage: 'M8 3c-1.6 1.6 1.6 3.4 0 5M12 3c-1.6 1.6 1.6 3.4 0 5M16 3c-1.6 1.6 1.6 3.4 0 5M4 12h16v5H4ZM6.5 17v3M17.5 17v3',
};

/**
 * The site header: a glass pill floating over the hero that docks to the top
 * edge once the page scrolls. On tablets and phones the links move into a
 * drawer that slides in from the right.
 */
@Component({
  selector: 'app-header',
  templateUrl: './header.html',
  styleUrl: './header.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:scroll)': 'onScroll()',
    '(window:keydown.escape)': 'close()',
  },
})
export class Header {
  protected readonly nav = NAV;
  protected readonly account = ACCOUNT;
  /** the Collections menu: the categories of the range; Segments opens a second level */
  protected readonly categories = RANGE.map((tab) => ({
    id: tab.id,
    label: tab.label,
    icon: CATEGORY_ICONS[tab.id] ?? CATEGORY_ICONS['segments'],
    links: tab.id === 'segments' ? tab.items.map((item) => ({ label: item.name, href: item.href })) : [],
  }));

  /** the page has been scrolled — dock the bar to the top edge */
  protected readonly stuck = signal(false);
  /** the drawer (tablet and phone) */
  protected readonly open = signal(false);
  /** the Collections list inside the drawer */
  protected readonly expanded = signal(false);
  /** id of the category whose links are folded open in the drawer (none at first) */
  protected readonly nestedOpen = signal('');
  /** a category was just chosen: keep the hover menu shut until the pointer has left it */
  protected readonly dismissed = signal(false);
  /** id of the section of the page now on screen — its link is highlighted */
  protected readonly current = signal('');

  protected readonly popups = inject(Popups);
  private readonly range = inject(RangeTab);
  private readonly document = inject(DOCUMENT);

  constructor() {
    const destroyRef = inject(DestroyRef);
    afterNextRender(() => {
      // a section counts as on screen while it crosses a line just above the middle of the window
      const ids = ['collections', ...this.nav.filter((link) => !link.external).map((link) => link.href.slice(1))];
      const onScreen = new Set<string>();
      const watcher = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) onScreen.add(entry.target.id);
            else onScreen.delete(entry.target.id);
          }
          // (if two overlap the line, the one further down the page wins)
          this.current.set(ids.filter((id) => onScreen.has(id)).pop() ?? '');
        },
        { rootMargin: '-45% 0px -54% 0px' },
      );
      for (const id of ids) {
        const section = this.document.getElementById(id);
        if (section) watcher.observe(section);
      }
      destroyRef.onDestroy(() => watcher.disconnect());
    });
  }

  protected onScroll(): void {
    const stuck = window.scrollY > 24;
    if (stuck !== this.stuck()) this.stuck.set(stuck);
  }

  protected close(): void {
    this.open.set(false);
    this.nestedOpen.set('');
  }

  /**
   * Login opens the sign-in popup over the page. (Its link to the shop's own login page is what a
   * browser without scripts follows — and what a new-tab click opens.)
   */
  protected login(event: MouseEvent): void {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.close();
    this.popups.show('auth');
  }

  /** A category was chosen in the Collections menu: show it in the range browser (the link scrolls there). */
  protected pick(id: string): void {
    this.range.id.set(id);
    this.dismiss();
  }

  /** Shut whichever menu the choice was made in. */
  protected dismiss(): void {
    this.close();
    this.dismissed.set(true);
    // (the hover menu also stays open while something inside it has focus)
    (this.document.activeElement as HTMLElement | null)?.blur();
  }
}
